import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'wouter';
import { v4 as uuidv4 } from 'uuid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleIcon from '@mui/icons-material/AddCircle';

import childSelector from '../childSelector';
import { loadChildren, addChild, deleteChild } from '../childrenReducer';
import transformer from '../../common/helpers/transformer';
import LoadingIndicator from '../../common/components/LoadingIndicator';
import type { AppDispatch } from '../../store';
import { initChild } from './initChild';
import { getClientPayloadType } from '../../common/helpers/payloadType';
import ConfirmationDialog from '../../common/components/ConfirmationDialog';
import ChildrenTable from './ChildrenTable';

function Children() {
  const [location, setLocation] = useLocation();
  const [selectedChildrenIds, setSelectedChildrenIds] = useState<string[]>([]);
  const dispatch: AppDispatch = useDispatch();

  const children = useSelector(childSelector.getChildren);
  const status = useSelector(childSelector.getStatus);

  const [searchValue, setSearchValue] = React.useState('');

  React.useEffect(() => {
    dispatch(loadChildren());
  }, []);

  return (
    <Stack px={3} py={4} spacing={2} height='100%' overflow='hidden'>
      <Stack direction='row' alignItems='center' width='100%'>
        <Typography fontSize='1.5rem'>Kinder</Typography>
        <Button
          sx={{ margin: '0 16px' }}
          aria-label='Kind hinzufügen'
          startIcon={<AddCircleIcon />}
          variant='contained'
          onClick={async () => {
            const id = uuidv4();
            const payload = await transformer.fromObjectToBase64WithEncryption({ state: false });

            await dispatch(addChild({ id, payload }));
            await dispatch(loadChildren());

            setLocation(`${location}/${id}`);
          }}
        >
          Kind hinzufügen
        </Button>

        <Stack direction='row' alignItems='center' ml='auto'>
          {selectedChildrenIds.length > 0 && (
            <ConfirmationDialog
              button={(onClick) => (
                <Button
                  variant='outlined'
                  startIcon={<DeleteIcon />}
                  sx={{ marginRight: 5 }}
                  onClick={(event) => {
                    event.stopPropagation();
                    onClick();
                  }}
                >
                  Kinder löschen
                </Button>
              )}
              dialogTitle='Ausgewählte Kinder löschen'
              dialogText={`Möchtest du die ${selectedChildrenIds.length} ${
                selectedChildrenIds.length === 1 ? 'ausgewählte' : 'ausgewählten'
              } Kinder wirklich löschen?`}
              dialogConfirmText='Löschen'
              onConfirm={async () => {
                await Promise.all(selectedChildrenIds.map((id) => dispatch(deleteChild(id))));
                await dispatch(loadChildren());
                setSelectedChildrenIds([]);
              }}
            />
          )}
          <TextField
            label='Suche'
            type='search'
            variant='standard'
            sx={{ width: '300px' }}
            onChange={(event) => setSearchValue(event.target.value)}
          />
        </Stack>
      </Stack>
      {status === 'loading' ? (
        <LoadingIndicator />
      ) : (
        <ChildrenTable
          searchValue={searchValue}
          children={children}
          selectedChildrenIds={selectedChildrenIds}
          setSelectedChildrenIds={setSelectedChildrenIds}
        />
      )}
    </Stack>
  );
}

export default Children;
